import { useState } from 'react'
import { toast } from 'sonner'
import { Pencil, Trash2 } from 'lucide-react'
import { ActionPopover } from '@/components/action-popover'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/loading-button'
import { formatApiErrorMessage } from '@/lib/api-error'
import { ChatMessageRow } from '@/features/storefront-chat/chat-message'
import { useChangeChatReply } from '@/features/storefront-chat/hooks'
import type { ChatMessage } from '@/features/storefront-chat/types'
import { ComposeTextarea } from '@/components/compose-field'

// What the edit route takes (StorefrontChatMessageEdit).
const MAX_REPLY_CHARS = 8000

// Shown on hover and keyboard focus; always shown on touch screens, which have no hover. There they
// overhang the time's line rather than make it taller.
const ACTION_BUTTON =
  'size-6 text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus-visible:opacity-100 group-hover/message:opacity-100 data-[state=open]:opacity-100 [@media(hover:none)]:-my-1.5 [@media(hover:none)]:size-7 [@media(hover:none)]:opacity-100'

/** One of our replies (the AI's or a person's), which staff can edit or delete (USA-1548). The
 * shopper's chat shows the new text marked "Edited"; a deleted reply leaves their chat. */
export function ChatReplyRow({
  conversationId,
  message,
  shopperName,
  readAt,
  me,
}: {
  conversationId: string
  message: ChatMessage
  shopperName: string
  readAt?: string | null
  me: { id: number; name: string } | undefined
}) {
  const change = useChangeChatReply(conversationId, me)
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState(message.body)
  const [deleting, setDeleting] = useState(false)
  const trimmed = text.trim()

  const startEditing = () => {
    setText(message.body)
    setEditing(true)
  }
  const save = () => {
    if (!trimmed || change.isPending) return
    if (trimmed === message.body) {
      setEditing(false)
      return
    }
    change.mutate(
      { messageId: message.id, body: trimmed },
      {
        onSuccess: () => setEditing(false),
        onError: (err) => toast.error(formatApiErrorMessage('Edit failed', err)),
      },
    )
  }

  const editor = editing && (
    <form
      className="flex w-full flex-col gap-1.5"
      onSubmit={(event) => {
        event.preventDefault()
        save()
      }}
    >
      <ComposeTextarea
        autoFocus
        value={text}
        maxLength={MAX_REPLY_CHARS}
        aria-label="Edit reply"
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault()
            setEditing(false)
          } else if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
            event.preventDefault()
            save()
          }
        }}
        disabled={change.isPending}
        // 16px on touch screens: iPhones zoom into any smaller text box when it is tapped.
        className="field-sizing-content max-h-72 min-h-20 resize-none rounded-2xl rounded-br-md bg-background text-base pointer-fine:text-sm"
      />
      <div className="flex justify-end gap-1.5">
        <Button type="button" size="sm" variant="ghost" disabled={change.isPending} onClick={() => setEditing(false)}>
          Cancel
        </Button>
        <LoadingButton type="submit" size="sm" loading={change.isPending} loadingText="Saving..." disabled={!trimmed}>
          Save
        </LoadingButton>
      </div>
    </form>
  )

  // System notes (a deleted reply among them) stay as they are; rendering them here keeps a delete's
  // error message when the reply turns into its note before the server answers.
  const changeable = message.author === 'ai' || message.author === 'staff'
  const actions = changeable && !editing && (
    <span className="ml-1 inline-flex translate-y-[3px] items-center">
      <Button type="button" variant="ghost" size="icon" className={ACTION_BUTTON} aria-label="Edit reply" onClick={startEditing}>
        <Pencil className="h-3 w-3" />
      </Button>
      <ActionPopover
        open={deleting}
        onOpenChange={setDeleting}
        align="end"
        title="Delete this reply?"
        confirmLabel="Delete"
        confirmVariant="destructive"
        pending={change.isPending}
        pendingLabel="Deleting..."
        onConfirm={(_note, close) =>
          change.mutate(
            { messageId: message.id, body: null },
            {
              onSuccess: close,
              onError: (err) => toast.error(formatApiErrorMessage('Delete failed', err)),
            },
          )
        }
        trigger={
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={`${ACTION_BUTTON} hover:text-destructive`}
            aria-label="Delete reply"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        }
      />
    </span>
  )

  return (
    <ChatMessageRow
      message={message}
      shopperName={shopperName}
      readAt={readAt}
      editor={(changeable && editor) || undefined}
      actions={actions || undefined}
    />
  )
}
